import React, { useRef, useState, useEffect } from "react"
import { useQuery } from "urql"

import { stringPriceToInteger } from "../../../utils/utils"
import CurrencyInput from "../../shared/CurrencyInput"
import LoadingSpinner from "../../shared/LoadingSpinner"
import { AnimatedModal } from "../../shared/Modal"
import { RadioWithLabel } from "../../shared/RadioButtons"
import Typography from "../../shared/Typography"

const DEFAULT_PAYMENT_METHOD_QUERY = `
  query($clientId: ID, $userId: ID, $practiceId: ID!) {
    defaultPaymentMethodPractice(clientId: $clientId, userId: $userId, practiceId: $practiceId) {
      brand
      last4
      expMonth
      expYear
    }
  }
`

const ConfirmPackageModal = ({
  showPackageConfirmModal,
  hideModal,
  onSave,
  client,
  amount,
  amountCents = 0,
  disabled,
  practice
}) => {
  const clientId = client?.__typename === "Client" ? client?.id : null
  const userId = client?.__typename === "User" ? client?.id : null
  const acceptsTips = !!practice?.acceptsTips
  const [tipOption, setTipOption] = useState(acceptsTips ? "" : "not-now")
  const [tipValue, setTipValue] = useState(0)
  const customTipInputRef = useRef(null)

  useEffect(() => {
    if (!showPackageConfirmModal) {
      setTipOption(acceptsTips ? "" : "not-now")
      setTipValue(0)
    }
  }, [showPackageConfirmModal, acceptsTips])

  useEffect(() => {
    if (tipOption === "custom-tip" && customTipInputRef.current) {
      customTipInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [tipOption])

  const [{ data, fetching, error }] = useQuery({
    query: DEFAULT_PAYMENT_METHOD_QUERY,
    variables: { clientId, userId, practiceId: practice?.id },
    pause: !client?.id || !practice?.hasPaymentProvider
  })

  if (error) console.error(error) // eslint-disable-line no-console

  const cardOnFile = data?.defaultPaymentMethodPractice
  const clientName = client?.firstName || "This client"
  const baseCents = parseInt(amountCents, 10) || 0

  const tippingOptions = [
    { value: "not-now", label: "Not now" },
    { value: "10%", label: `10% • $${((0.1 * baseCents) / 100).toFixed(2)}` },
    { value: "15%", label: `15% • $${((0.15 * baseCents) / 100).toFixed(2)}` },
    { value: "20%", label: `20% • $${((0.2 * baseCents) / 100).toFixed(2)}` },
    { value: "custom-tip", label: "Custom Tip" }
  ]

  const handleSave = () => {
    if (acceptsTips && tipOption === "") return
    onSave(tipValue)
  }

  return (
    <AnimatedModal
      visible={showPackageConfirmModal}
      hideModal={hideModal}
      header={cardOnFile ? "Charge card on file now" : "No card on file"}
      showFooter={true}
      actionButtonCopy={cardOnFile ? `Charge ${amount} now` : "Continue & book"}
      actionButtonDataTestid="confirm-package-continue"
      saveDisabled={disabled || (acceptsTips && tipOption === "")}
      onSave={handleSave}>
      {fetching ? (
        <LoadingSpinner />
      ) : cardOnFile ? (
        <p>
          Booking this package will charge {clientName}&apos;s card on file now ({cardOnFile.brand} ending in{" "}
          {cardOnFile.last4}) for {amount}
          {tipValue > 0 ? ` plus a tip of $${(tipValue / 100).toFixed(2)}` : ""}. Click cancel to go back.
        </p>
      ) : (
        <p>{clientName} doesn&apos;t have a card on file, but you can still book this package and add a card later.</p>
      )}
      {acceptsTips && !fetching && (
        <div className="mt-4">
          <Typography variant="h5" className="mb-4">
            Add a tip
          </Typography>
          <div className="flex flex-col gap-2">
            {tippingOptions.map((option) => (
              <RadioWithLabel
                key={option.value}
                value={option.value}
                name="package-tip"
                label={option.label}
                checked={tipOption === option.value}
                onChange={() => {
                  setTipOption(option.value)
                  if (option.value === "not-now") {
                    setTipValue(0)
                    return
                  }
                  if (option.value === "custom-tip") {
                    setTipValue(Math.round(baseCents * 0.1))
                    return
                  }
                  setTipValue(Math.round((baseCents * parseInt(option.value, 10)) / 100))
                }}
              />
            ))}
          </div>
          {tipOption === "custom-tip" && (
            <div ref={customTipInputRef}>
              <CurrencyInput
                label="Custom tip"
                value={tipValue / 100}
                onChange={(e) => {
                  if (e.target.value === "") {
                    setTipValue(0)
                    return
                  }
                  setTipValue(stringPriceToInteger(e.target.value))
                }}
                className="mt-4"
                autoFocus
              />
            </div>
          )}
        </div>
      )}
    </AnimatedModal>
  )
}

export default ConfirmPackageModal
